import { useState } from 'react';
import { AlertTriangle, CheckCircle, X, Flag, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { confirmNegotiationCompletion } from '@/services/negotiationCredits';
import CancelNegotiationModal from '@/components/marketplace/CancelNegotiationModal';
import ReportNegotiationModal from '@/components/marketplace/ReportNegotiationModal';
import type { PendingConfirmation } from '@/services/pendingConfirmations';

interface PendingConfirmationModalProps {
  confirmation: PendingConfirmation;
  onResolved: () => void;
}

export default function PendingConfirmationModal({
  confirmation,
  onResolved,
}: PendingConfirmationModalProps) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const daysSinceConfirmation = Math.floor(
    (Date.now() - new Date(confirmation.confirmed_at).getTime()) / (1000 * 60 * 60 * 24)
  );

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      const result = await confirmNegotiationCompletion(
        confirmation.negotiation_id,
        confirmation.user_role
      );

      if (!result.success) {
        toast.error(result.error || 'Erro ao confirmar conclusão');
        return;
      }

      toast.success('Negociação concluída com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['pending-confirmations'] });
      queryClient.invalidateQueries({ queryKey: ['pending-ratings'] });
      onResolved();
    } catch (error: any) {
      toast.error('Erro ao confirmar conclusão');
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['pending-confirmations'] });
    onResolved();
  };

  const handleReportClose = () => {
    setShowReport(false);
    queryClient.invalidateQueries({ queryKey: ['pending-confirmations'] });
    onResolved();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-background"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      >
        <div className="flex flex-col items-center justify-center text-center p-8 max-w-md mx-auto">
          <div className="w-24 h-24 rounded-full bg-warning/20 flex items-center justify-center mb-6">
            <AlertTriangle className="w-12 h-12 text-warning" />
          </div>

          <h1 className="text-3xl font-bold text-foreground mb-3">
            Ação Necessária
          </h1>

          <p className="text-muted-foreground text-lg mb-4">
            A outra parte confirmou a conclusão da negociação há{' '}
            <span className="font-semibold text-foreground">{daysSinceConfirmation} dias</span>.
          </p>

          <div className="w-full bg-muted/50 rounded-lg p-4 mb-6 border border-border">
            <p className="text-sm text-muted-foreground mb-1">Negociação:</p>
            <p className="text-foreground font-medium">{confirmation.listing_title}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {confirmation.user_role === 'buyer' ? 'Vendedor' : 'Comprador'}:{' '}
              <span className="text-foreground">{confirmation.other_user_name}</span>
            </p>
          </div>

          <p className="text-sm text-muted-foreground mb-8">
            Você precisa agir para continuar usando o sistema. Escolha uma das opções abaixo:
          </p>

          <div className="w-full space-y-3">
            <Button
              onClick={handleConfirm}
              disabled={confirming}
              className="w-full gap-2 bg-success text-success-foreground hover:bg-success/90"
            >
              {confirming ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Confirmar Conclusão
            </Button>

            <Button
              onClick={() => setShowCancel(true)}
              variant="outline"
              className="w-full gap-2 border-destructive text-destructive hover:bg-destructive/10"
            >
              <X className="w-4 h-4" />
              Cancelar Negociação
            </Button>

            <Button
              onClick={() => setShowReport(true)}
              variant="outline"
              className="w-full gap-2"
            >
              <Flag className="w-4 h-4" />
              Reportar Problema
            </Button>
          </div>
        </div>
      </div>

      <CancelNegotiationModal
        open={showCancel}
        onOpenChange={setShowCancel}
        negotiationId={confirmation.negotiation_id}
        onCancelled={handleCancelSuccess}
        onSuccess={handleCancelSuccess}
      />

      <ReportNegotiationModal
        open={showReport}
        onOpenChange={handleReportClose}
        negotiationId={confirmation.negotiation_id}
        reportedUserId={confirmation.other_user_id}
        reportedUserName={confirmation.other_user_name}
      />
    </>
  );
}
